/**
 * AiAccuracyTrendCard — Iter-90 · Operations Command Center.
 *
 * Line chart of the last 30 days of Gemini violation-classifier accuracy,
 * bucketed into 4 weekly points. Data source is the accept/override log
 * written by `/api/violations/ai-suggestion-log` on every submitted report.
 *
 * Metrics surfaced:
 *   • Total suggestions in the window
 *   • Accepted (%) and Overridden (%)
 *   • Week-over-week arrow (↑ improving · ↓ declining · → flat)
 *
 * Ops Directors see the full 30-day chart here. The CEO Executive HQ
 * receives only the current-week accept rate as a single tile — that tile
 * is rendered inside ExecutiveCommandCenter (per architecture rule this
 * card must render live before the exec tile is wired).
 */
import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Sparkles, ArrowUpRight, ArrowDownRight, Minus, Star, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AiAccuracyTrendCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const canGrade = ['admin', 'company_manager', 'operations_director'].includes(user?.role);
  const canExport = ['admin', 'company_manager', 'operations_director'].includes(user?.role);

  const downloadRetrainingCsv = async () => {
    try {
      const r = await fetch(`${API}/api/operations/ai-retraining-export.csv`, { credentials: 'include' });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        toast.error(j.detail || `Export failed (${r.status})`);
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
      link.download = `ai_retraining_candidates_${stamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Retraining candidates CSV downloaded');
    } catch (e) { toast.error(e.message); }
  };

  const reload = async () => {
    try {
      const r = await fetch(`${API}/api/operations/ai-accuracy-trend`, { credentials: 'include' });
      if (!r.ok) return;
      const d = await r.json();
      setData(d);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const summary = data?.summary || {
    total_suggestions: 0, accepted: 0, overridden: 0,
    accept_rate: 0, override_rate: 0, current_week_accept_rate: 0,
    wow_delta_pts: 0, trend: 'flat',
    low_confidence_count: 0, low_confidence_rate: 0, avg_note_quality: null,
  };
  const TrendIcon = summary.trend === 'up' ? ArrowUpRight
                   : summary.trend === 'down' ? ArrowDownRight
                   : Minus;
  const trendColor = summary.trend === 'up' ? '#10b981'
                    : summary.trend === 'down' ? '#ef4444'
                    : '#a1a1aa';

  return (
    <Card className="bg-zinc-900 border border-zinc-800 p-4" data-testid="ops-ai-accuracy-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-amber-400" />
          <h3 className="text-white font-black text-base" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            GEMINI ACCURACY TREND
          </h3>
          <Badge className="bg-zinc-800 text-[10px] uppercase tracking-widest">Last 30 days</Badge>
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <span className="text-zinc-400" data-testid="ops-ai-total">
            Suggestions: <b className="text-white">{summary.total_suggestions}</b>
          </span>
          <span className="text-emerald-400" data-testid="ops-ai-accept-rate">
            Accepted: <b>{summary.accept_rate}%</b>
          </span>
          <span className="text-red-400" data-testid="ops-ai-override-rate">
            Overridden: <b>{summary.override_rate}%</b>
          </span>
          <span className="flex items-center gap-1 font-semibold" style={{ color: trendColor }} data-testid="ops-ai-trend">
            <TrendIcon className="h-3.5 w-3.5" />
            {summary.wow_delta_pts > 0 ? '+' : ''}{summary.wow_delta_pts}pts WoW
          </span>
        </div>
      </div>

      {loading ? (
        <div className="h-56 flex items-center justify-center text-zinc-500 text-sm">Loading…</div>
      ) : summary.total_suggestions === 0 ? (
        <div className="h-56 flex items-center justify-center border border-dashed border-zinc-700 rounded-lg text-zinc-500 text-sm" data-testid="ops-ai-empty">
          No AI suggestions logged in the last 30 days yet — data appears once agents submit reports with Gemini suggestions.
        </div>
      ) : (
        <>
          <div style={{ height: 240 }} data-testid="ops-ai-accuracy-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.points || []} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                <XAxis dataKey="label" stroke="#a1a1aa" fontSize={11} />
                <YAxis stroke="#a1a1aa" fontSize={11} domain={[0, 100]} unit="%" />
                <Tooltip
                  contentStyle={{ backgroundColor: '#0a0a0a', border: '1px solid #3f3f46', fontSize: 12, color: '#fafafa' }}
                  labelStyle={{ color: '#C9A962', fontWeight: 700 }}
                  formatter={(v, name) => [`${v}%`, name]}
                />
                <Legend wrapperStyle={{ fontSize: 11, color: '#a1a1aa' }} />
                <Line type="monotone" dataKey="accept_rate"   name="Accepted %"   stroke="#10b981" strokeWidth={2.5} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="override_rate" name="Overridden %" stroke="#ef4444" strokeWidth={2.5} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {/* Iter-90 · Director-tier region breakdown — which regions
              accept most vs override most. Region-scoped viewers will
              only see their own region here (server-side filtered). */}
          {(data?.by_region || []).length > 0 && (
            <div className="mt-3" data-testid="ops-ai-by-region">
              <p className="text-[10px] uppercase tracking-widest text-[#C9A962] font-bold mb-2">By Region</p>
              <div className="space-y-1.5">
                {data.by_region.map((rg) => (
                  <div
                    key={rg.region_id}
                    className="flex items-center gap-2 p-1.5 rounded bg-zinc-950 border border-zinc-800"
                    data-testid={`ops-ai-region-${rg.region_id}`}
                  >
                    <span className="text-[11px] text-white font-semibold w-28 truncate">{rg.region_name}</span>
                    <span className="text-[10px] text-zinc-500 w-16">n={rg.total}</span>
                    <div className="flex-1 h-2 rounded-full bg-zinc-800 overflow-hidden flex">
                      <div style={{ width: `${rg.accept_rate}%`, backgroundColor: '#10b981' }} />
                      <div style={{ width: `${rg.override_rate}%`, backgroundColor: '#ef4444' }} />
                    </div>
                    <span className="text-[10px] font-mono text-emerald-400 w-10 text-right">{rg.accept_rate}%</span>
                    <span className="text-[10px] font-mono text-red-400 w-10 text-right">{rg.override_rate}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Iter-91 · Low-confidence metrics. Total low-confidence triggers
              this window, current rate, and avg agent-note quality grade
              (Ops Director-editable). Also lists the 30 most-recent
              low-conf logs so Directors can spot pattern issues fast. */}
          <div className="mt-3" data-testid="ops-ai-low-conf-section">
            <div className="flex items-baseline justify-between mb-2">
              <p className="text-[10px] uppercase tracking-widest text-red-400 font-bold">Low-Confidence (&lt;40%) · Retraining Signal</p>
              <div className="flex items-center gap-3 text-[11px]">
                <span className="text-zinc-400">
                  Total 30d: <b className="text-white" data-testid="ops-ai-low-conf-total">{summary.low_confidence_count}</b>
                </span>
                <span className="text-red-400">
                  Rate: <b data-testid="ops-ai-low-conf-rate">{summary.low_confidence_rate}%</b>
                </span>
                <span className="text-zinc-400">
                  Note quality: <b className="text-white" data-testid="ops-ai-note-quality">
                    {summary.avg_note_quality != null ? `${summary.avg_note_quality}/5` : '—'}
                  </b>
                </span>
              </div>
            </div>
            {(data?.low_confidence_logs || []).length === 0 ? (
              <p className="text-[11px] text-zinc-500 italic">
                No low-confidence classifications yet — Gemini has stayed above 40% on every image.
              </p>
            ) : (
              <div className="max-h-40 overflow-y-auto space-y-1" data-testid="ops-ai-low-conf-list">
                {data.low_confidence_logs.map((l) => (
                  <div
                    key={l.log_id}
                    className="flex items-center gap-2 p-1.5 rounded bg-zinc-950 border border-red-900/40 text-[11px]"
                    data-testid={`ops-ai-low-conf-row-${l.log_id}`}
                  >
                    <span className="text-white font-semibold truncate w-24" title={l.agent_name}>{l.agent_name || '—'}</span>
                    <span className="font-mono text-red-400 w-10 text-right">{Math.round((l.confidence || 0) * 100)}%</span>
                    <span className="text-zinc-500 truncate flex-1" title={l.low_conf_note || ''}>
                      {l.low_conf_note ? `“${l.low_conf_note}”` : <em className="text-zinc-600">(no note captured)</em>}
                    </span>
                    {/* Iter-93 · 1-5 star grading control (Ops Director+). */}
                    <StarRater
                      logId={l.log_id}
                      value={l.note_quality_score || 0}
                      canRate={canGrade}
                      onSaved={reload}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Iter-93 · Ranked retraining candidates (lowest grade first). */}
            {(data?.retraining_candidates || []).length > 0 && (
              <div className="mt-3" data-testid="ops-ai-retraining-list">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] uppercase tracking-widest text-purple-400 font-bold">
                    Retraining Queue · Lowest Grades First
                  </p>
                  {canExport && (
                    <button
                      type="button"
                      onClick={downloadRetrainingCsv}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-purple-700/60 bg-purple-900/20 hover:bg-purple-900/40 transition text-[10px] font-bold uppercase tracking-widest text-purple-200"
                      title="Download graded low-quality notes (≤3★) for ML retraining"
                      data-testid="ops-ai-retraining-download-btn"
                    >
                      <Download className="h-3 w-3" />
                      Download Retraining Candidates
                    </button>
                  )}
                </div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {data.retraining_candidates.map((c) => (
                    <div key={c.log_id} className="flex items-center gap-2 p-1.5 rounded bg-purple-900/10 border border-purple-800/40 text-[11px]" data-testid={`ops-ai-retraining-row-${c.log_id}`}>
                      <span className="text-white font-semibold w-24 truncate">{c.agent_name}</span>
                      <span className="font-mono text-red-400 w-10 text-right">{Math.round((c.confidence || 0) * 100)}%</span>
                      <span className="text-zinc-400 truncate flex-1">“{c.low_conf_note || '—'}”</span>
                      <span className="font-bold text-purple-300 w-8 text-right" data-testid={`ops-ai-retraining-score-${c.log_id}`}>{c.note_quality_score}★</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

// Iter-93 · 1-5 star grading control for Ops Directors. Persists to
// /api/violations/ai-suggestion-log/{log_id}/quality so the trend
// endpoint's retraining_candidates list re-ranks on next reload.
function StarRater({ logId, value, canRate, onSaved }) {
  const [hover, setHover] = useState(0);
  const submit = async (n) => {
    try {
      const r = await fetch(`${API}/api/violations/ai-suggestion-log/${logId}/quality`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note_quality_score: n }),
      });
      if (r.ok) {
        toast.success(`Graded ${n}★`);
        onSaved?.();
      } else {
        const j = await r.json().catch(() => ({}));
        toast.error(j.detail || 'Grade failed');
      }
    } catch (e) { toast.error(e.message); }
  };
  if (!canRate) {
    return (
      <span className="flex items-center gap-0.5" data-testid={`ops-ai-grade-readonly-${logId}`}>
        {[1,2,3,4,5].map((n) => (
          <Star key={n} className={`h-3 w-3 ${n <= (value || 0) ? 'text-purple-400 fill-purple-400' : 'text-zinc-700'}`} />
        ))}
      </span>
    );
  }
  return (
    <span
      className="flex items-center gap-0.5"
      onMouseLeave={() => setHover(0)}
      data-testid={`ops-ai-grade-${logId}`}
    >
      {[1,2,3,4,5].map((n) => (
        <button
          type="button"
          key={n}
          onMouseEnter={() => setHover(n)}
          onClick={() => submit(n)}
          className="p-0"
          title={`Rate ${n} star${n>1?'s':''}`}
          data-testid={`ops-ai-grade-${logId}-${n}`}
        >
          <Star className={`h-3.5 w-3.5 transition-colors ${
            n <= (hover || value || 0) ? 'text-purple-400 fill-purple-400' : 'text-zinc-700 hover:text-purple-300'
          }`} />
        </button>
      ))}
    </span>
  );
}

